import { useMemo } from 'react';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import passwordDictionaryList from '~/utilities/passwordDictionary.json';

const passwordDictionary = new Set(passwordDictionaryList);

const PASSWORD_MAX_LENGTH = 64;
const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_REPEAT_SEQUENCE_LENGTH = 3;

const generateSequences = (alphabet: string[], sequenceLength: number) => {
  return alphabet
    .slice(0, -sequenceLength + 1)
    .map((_, i) => alphabet.slice(i, i + sequenceLength).join(''));
};

type PatientInfo = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

type CurrentPasswordTranslations = {
  currentPassword?: {
    required?: string;
  };
};

type NewPasswordTranslations = {
  newPassword?: {
    required?: string;
    minLength?: ({ count }: { count: number }) => string;
    maxLength?: ({ count }: { count: number }) => string;
    trim?: string;
    repetitiveSequence?: ({ count }: { count: number }) => string;
    context?: string;
    dictionary?: string;
    mustMatch?: string;
    repeatRequired?: string;
    differentFromCurrent?: string;
  };
};

type OptionsWithCurrentPassword = {
  validateCurrentPassword: true;
  hasNewPassword?: false;
  patient: undefined;
  translations?: CurrentPasswordTranslations;
};

type OptionsWithNewPassword = {
  validateCurrentPassword: false;
  hasNewPassword: true;
  patient?: PatientInfo;
  translations?: NewPasswordTranslations;
};

type OptionsWithAllValidations = {
  validateCurrentPassword: true;
  hasNewPassword: true;
  patient?: PatientInfo;
  translations?: CurrentPasswordTranslations & NewPasswordTranslations;
};

export const usePasswordValidation = ({
  validateCurrentPassword,
  hasNewPassword,
  patient,
  translations,
}:
  | (OptionsWithCurrentPassword | OptionsWithNewPassword)
  | OptionsWithAllValidations) => {
  const validationSchema = useMemo(() => {
    const schema: Record<string, z.ZodType> = {};

    if (validateCurrentPassword) {
      schema.currentPassword = z
        .string({
          error: translations?.currentPassword?.required ?? 'Required',
        })
        .trim()
        .min(1, {
          error: translations?.currentPassword?.required ?? 'Required',
        });
    }

    if (hasNewPassword) {
      schema.newPassword = z
        .string(translations?.newPassword?.required ?? 'Required')
        .min(PASSWORD_MIN_LENGTH, {
          error:
            translations?.newPassword?.minLength?.({
              count: PASSWORD_MIN_LENGTH,
            }) ?? `Minimum length is ${PASSWORD_MIN_LENGTH}`,
        })
        .max(PASSWORD_MAX_LENGTH, {
          error:
            translations?.newPassword?.maxLength?.({
              count: PASSWORD_MAX_LENGTH,
            }) ?? `Maximum length is ${PASSWORD_MAX_LENGTH}`,
        })
        .refine(
          (password) => {
            return password.trim() === password;
          },
          {
            error:
              translations?.newPassword?.trim ??
              'Remove leading and trailing spaces',
          },
        )
        .refine(
          (password) => {
            const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');
            const digits = '0123456789'.split('');
            const letterSequences = generateSequences(
              alphabet,
              PASSWORD_REPEAT_SEQUENCE_LENGTH,
            );
            const digitSequences = generateSequences(
              digits,
              PASSWORD_REPEAT_SEQUENCE_LENGTH,
            );
            const regex = new RegExp(
              `(.)\\1\\1|${letterSequences.join('|')}|${digitSequences.join('|')}`,
              'gi',
            );

            return !regex.test(password);
          },
          {
            error:
              translations?.newPassword?.repetitiveSequence?.({
                count: PASSWORD_REPEAT_SEQUENCE_LENGTH,
              }) ??
              `Cannot contain repetitive sequences of ${PASSWORD_REPEAT_SEQUENCE_LENGTH} characters`,
          },
        )
        .refine(
          (password) => {
            const contextWords = [
              'lactate',
              patient?.firstName ?? undefined,
              patient?.lastName ?? undefined,
              patient?.email ?? undefined,
            ].filter(Boolean);

            return !contextWords.some((word) =>
              password.toLowerCase().includes(word.toLowerCase()),
            );
          },
          {
            error:
              translations?.newPassword?.context ??
              'Password cannot contain personal context (name, email, etc.)',
          },
        )
        .refine((password) => !passwordDictionary.has(password), {
          error:
            translations?.newPassword?.dictionary ??
            'Password cannot be a common word or in the dictionary',
        });

      // Repeat new password validation
      schema.repeatNewPassword = z
        .string(translations?.newPassword?.repeatRequired ?? 'Required')
        .trim()
        .min(1, translations?.newPassword?.repeatRequired ?? 'Required');
    }

    let finalSchema = z.object(schema);

    if (hasNewPassword) {
      finalSchema = finalSchema
        .refine((data) => data.newPassword === data.repeatNewPassword, {
          error: translations?.newPassword?.mustMatch ?? 'Passwords must match',
          path: ['repeatNewPassword'],
        })
        .refine(
          (data) => {
            // If current password is not being validated, skip this check
            if (!validateCurrentPassword) {
              return true;
            }

            return data.newPassword !== data.currentPassword;
          },
          {
            error:
              translations?.newPassword?.differentFromCurrent ??
              'New password must be different from current password',
            path: ['newPassword'],
          },
        );
    }

    return finalSchema;
  }, [validateCurrentPassword, hasNewPassword, translations, patient]);

  return toFormikValidationSchema(validationSchema);
};
